import { useEffect, useRef, useState } from "react";
import { api, setToken, getToken, clearToken } from "rsa/api";
import { useSocket } from "rsa/useSocket";
import MapView from "rsa/MapView";
import "rsa/rsa.scss";
import "./vendor.scss";

/** Web-Audio two-tone siren (no audio asset needed). */
function useSiren() {
  const ctxRef = useRef(null);
  const nodesRef = useRef(null);
  const ensure = () => {
    if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    if (ctxRef.current.state === "suspended") ctxRef.current.resume();
    return ctxRef.current;
  };
  const start = () => {
    const ctx = ensure();
    if (nodesRef.current) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    osc.type = "sawtooth"; osc.frequency.value = 700;
    lfo.type = "sine"; lfo.frequency.value = 3; lfoGain.gain.value = 250;
    lfo.connect(lfoGain); lfoGain.connect(osc.frequency);
    gain.gain.value = 0.14;
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(); lfo.start();
    nodesRef.current = { osc, lfo, gain };
  };
  const stop = () => {
    const n = nodesRef.current;
    if (n) { try { n.osc.stop(); n.lfo.stop(); } catch {} nodesRef.current = null; }
  };
  useEffect(() => () => stop(), []);
  return { start, stop, prime: ensure };
}

export default function VendorPortal() {
  const [token, setTok] = useState(getToken("vendor"));
  const [me, setMe] = useState(null);
  const [leads, setLeads] = useState([]);
  const [active, setActive] = useState(null);      // claimed request I'm serving
  const [alarm, setAlarm] = useState(null);        // the lead currently alarming
  const [toast, setToast] = useState("");
  const siren = useSiren();

  // login form
  const [email, setEmail] = useState("javed1187@gmail.com");
  const [password, setPassword] = useState("javed@123");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 3500); };

  const login = async () => {
    setBusy(true); setErr("");
    try {
      siren.prime();                                // unlock audio on the user gesture
      const r = await api("POST", "/auth/login", { body: { email, password } });
      if (!r.user.vendorId) throw new Error("This account is not a vendor.");
      setToken("vendor", r.token); setTok(r.token);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const logout = () => { clearToken("vendor"); setTok(null); setMe(null); setLeads([]); setActive(null); };

  const loadAll = async () => {
    if (!token) return;
    try {
      setMe(await api("GET", "/vendors/me", { token }));
      setLeads(await api("GET", "/rsa/leads", { token }));
    } catch (e) { if (e.status === 401) logout(); }
  };
  useEffect(() => { loadAll(); }, [token]); // eslint-disable-line

  useSocket(token, {
    "lead:new": (lead) => {
      setLeads((prev) => (prev.some((l) => l.lead_id === lead.lead_id) ? prev : [lead, ...prev]));
      setAlarm(lead); siren.start();
    },
    "lead:removed": ({ leadId }) => {
      setLeads((prev) => prev.filter((l) => l.lead_id !== leadId));
      setAlarm((a) => { if (a && a.lead_id === leadId) { siren.stop(); return null; } return a; });
    },
    "request:cancelled": ({ requestId }) => {
      setActive((a) => (a && a.id === requestId ? { ...a, status: "cancelled" } : a));
      flash("A request was cancelled by the customer.");
    },
  });

  const accept = async (lead) => {
    siren.stop(); setAlarm(null);
    try {
      const req = await api("POST", `/rsa/leads/${lead.lead_id}/accept`, { token });
      setActive(req); setLeads([]); flash("You won the lead! 🎉");
    } catch (e) {
      if (e.status === 409) { setLeads((p) => p.filter((l) => l.lead_id !== lead.lead_id)); flash("Another vendor claimed it first."); }
      else flash(e.message);
    }
  };
  const dismiss = () => { siren.stop(); setAlarm(null); };

  const setAvailability = async (status) => {
    const r = await api("PATCH", "/vendors/me", { token, body: { availabilityStatus: status } });
    setMe((m) => ({ ...m, availability_status: r.availability_status }));
  };
  const advance = async (next) => {
    const { request } = await api("POST", `/rsa/requests/${active.id}/status`, { token, body: { status: next } });
    setActive(request);
    if (next === "completed") flash(`Job complete — ₹${request.final_fee || request.estimated_fee} collected (cashless).`);
  };

  // ---------- login screen ----------
  if (!token)
    return (
      <div className="avah-rsa vendor">
        <div className="avah-rsa__top" style={{ background: "linear-gradient(135deg,#14181f,#2a3340)" }}>
          <h1>🔧 Avahan Partner Portal</h1><p>Collision-care vendor console</p>
        </div>
        <div className="avah-rsa__shell">
          <div className="avah-rsa__card">
            <label className="avah-rsa__label">Email</label>
            <input className="avah-rsa__input" value={email} onChange={(e) => setEmail(e.target.value)} />
            <label className="avah-rsa__label" style={{ marginTop: 12 }}>Password</label>
            <input className="avah-rsa__input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
            <button className="avah-rsa__btn avah-rsa__btn--primary" style={{ marginTop: 16, background: "#14181f" }}
              disabled={busy} onClick={login}>{busy ? "Signing in…" : "Sign in"}</button>
            {err && <div className="avah-rsa__err">{err}</div>}
            <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 0 }}>Demo: javed1187@gmail.com / javed@123</p>
          </div>
        </div>
      </div>
    );

  const online = me?.availability_status === "online";
  return (
    <div className="avah-rsa vendor">
      <div className="vendor__bar">
        <div>
          <b>{me?.business_name || "Loading…"}</b>
          <div className="vendor__sub">⭐ {me?.avg_rating} · {me?.total_reviews} reviews · {me?.service_radius_km} km radius</div>
        </div>
        <div className="avah-rsa__row">
          <button className={`vendor__toggle ${online ? "vendor__toggle--on" : ""}`}
            onClick={() => setAvailability(online ? "offline" : "online")}>
            <span className="vendor__dot" /> {online ? "Online" : "Offline"}
          </button>
          <button className="avah-rsa__btn avah-rsa__btn--ghost" style={{ width: "auto", padding: "8px 12px" }} onClick={logout}>Exit</button>
        </div>
      </div>

      <div className="avah-rsa__shell" style={{ maxWidth: 560 }}>
        {active ? (
          <ActiveJob req={active} onAdvance={advance} />
        ) : (
          <>
            <h3 style={{ margin: "18px 4px 6px" }}>Incoming leads {leads.length ? `(${leads.length})` : ""}</h3>
            {!online && <div className="avah-rsa__dev">You're offline — go Online to receive emergency leads.</div>}
            {online && leads.length === 0 && (
              <div className="avah-rsa__card" style={{ textAlign: "center", color: "#6b7280" }}>
                Waiting for emergency requests near you…
              </div>
            )}
            {leads.map((l) => (
              <LeadCard key={l.lead_id} lead={l} onAccept={() => accept(l)} />
            ))}
          </>
        )}
      </div>

      {alarm && <AlarmOverlay lead={alarm} onAccept={() => accept(alarm)} onDismiss={dismiss} />}
      {toast && <div className="vendor__toast">{toast}</div>}
    </div>
  );
}

function LeadCard({ lead, onAccept }) {
  return (
    <div className="avah-rsa__card vendor__lead">
      <div className="avah-rsa__row" style={{ justifyContent: "space-between" }}>
        <b>{lead.service_type_name}</b>
        <span className={`avah-rsa__pill ${lead.severity === "critical" ? "avah-rsa__pill--live" : ""}`}>{lead.severity}</span>
      </div>
      <div className="vendor__sub" style={{ margin: "6px 0" }}>
        {lead.distance_km} km away · {lead.requester_name}
        {lead.registration_number && <> · {lead.brand} {lead.model} ({lead.registration_number})</>}
      </div>
      {lead.description && <div className="vendor__sub">“{lead.description}”</div>}
      <button className="avah-rsa__btn avah-rsa__btn--primary" style={{ marginTop: 10 }} onClick={onAccept}>Accept lead</button>
    </div>
  );
}

function AlarmOverlay({ lead, onAccept, onDismiss }) {
  return (
    <div className="vendor__alarm">
      <div className="vendor__alarm-inner">
        <div className="vendor__alarm-badge">🚨 EMERGENCY</div>
        <h2>{lead.service_type_name}</h2>
        <div className="vendor__alarm-meta">
          <span>📍 {lead.distance_km} km away</span>
          <span className={lead.severity === "critical" ? "crit" : ""}>⚠ {lead.severity}</span>
          {lead.ambulance_requested && <span className="crit">🚑 ambulance</span>}
          {lead.fire_requested && <span className="crit">🚒 fire</span>}
          {lead.police_requested && <span className="crit">👮 police</span>}
        </div>
        <div className="vendor__alarm-veh">
          {lead.requester_name}
          {lead.registration_number && <> · {lead.brand} {lead.model} · <b>{lead.registration_number}</b></>}
        </div>
        {lead.pickup_latitude && (
          <div style={{ margin: "12px 0", borderRadius: 12, overflow: "hidden" }}>
            <MapView pickup={{ lat: +lead.pickup_latitude, lng: +lead.pickup_longitude }} height={160} />
          </div>
        )}
        <button className="avah-rsa__btn avah-rsa__btn--primary vendor__alarm-accept" onClick={onAccept}>
          ✅ ACCEPT &amp; RESPOND
        </button>
        <button className="vendor__alarm-dismiss" onClick={onDismiss}>Not now</button>
      </div>
    </div>
  );
}

const STEPS = [["enroute", "Start — On the way"], ["arrived", "Mark Arrived"], ["in_progress", "Start Work"], ["completed", "Complete & Collect"]];
function ActiveJob({ req, onAdvance }) {
  const cancelled = req.status === "cancelled";
  const nextIdx = STEPS.findIndex(([s]) => s === req.status) + 1;
  const next = STEPS[nextIdx] || (req.status === "claimed" ? STEPS[0] : null);
  return (
    <div className="avah-rsa__card" style={{ marginTop: 18 }}>
      <div className="avah-rsa__row" style={{ justifyContent: "space-between" }}>
        <b>{req.request_ref} · {req.service_type_name}</b>
        <span className={`avah-rsa__pill ${cancelled ? "" : "avah-rsa__pill--ok"}`}>{req.status}</span>
      </div>
      <div className="vendor__sub" style={{ margin: "8px 0" }}>
        {req.requester_type === "guest" ? req.guest_name : "Registered customer"} · {req.pickup_address || "shared location"}
        {req.estimated_fee && <> · est. ₹{Math.round(req.estimated_fee)}</>}
      </div>
      {req.pickup_latitude && (
        <div style={{ margin: "10px 0", borderRadius: 12, overflow: "hidden" }}>
          <MapView pickup={{ lat: +req.pickup_latitude, lng: +req.pickup_longitude }}
            vendors={req.vendor_lat ? [{ id: req.assigned_vendor_id, latitude: req.vendor_lat, longitude: req.vendor_lng, business_name: "You" }] : []}
            highlightVendorId={req.assigned_vendor_id} height={180} />
        </div>
      )}
      {cancelled ? (
        <div className="avah-rsa__err">This request was cancelled.</div>
      ) : next ? (
        <button className="avah-rsa__btn avah-rsa__btn--primary" onClick={() => onAdvance(next[0])}>{next[1]}</button>
      ) : (
        <div className="avah-rsa__pill avah-rsa__pill--ok">Job completed — payment settled 💳</div>
      )}
    </div>
  );
}
